import { uniq } from 'lodash-es'
import { isNonEmptyArray } from '#app/lib/boolean_tests'
import type { EntityDraft } from '#app/types/entity'
import type { SerializedEntity } from '#entities/lib/entities'
import type { WdEntityUri } from '#server/types/entity'

// Sorted by display order
const mostlyTextWorksRoles = [
  'wdt:P98', // editor
  'wdt:P50', // author
  'wdt:P110', // illustrator
] as const

// See https://git.inventaire.io/inventaire/issues/765
const manga = [
  'wdt:P50', // author
  'wdt:P110', // illustrator
  'wdt:P10837', // penciller
] as const

// Sorted by display order
const drawnWorksRoles = [
  'wdt:P58', // scenarist
  'wdt:P10837', // penciller
  'wdt:P6338', // colorist
  'wdt:P9191', // letterer
  'wdt:P10836', // inker
] as const

export const editionAuthorRoles = [
  'wdt:P655', // translator
  'wdt:P2679', // author of foreword
  'wdt:P2680', // author of afterword
  'wdt:P110', // illustrator
  'wdt:P736', // cover artist
] as const

export const workAuthorProperties = uniq([ ...mostlyTextWorksRoles, ...drawnWorksRoles ])
export type WorkAuthorProperty = typeof workAuthorProperties[number]
export type EditionAuthorProperty = typeof editionAuthorRoles[number]
export type AuthorProperty = WorkAuthorProperty | EditionAuthorProperty

export const authorRoleProperties = uniq([ ...mostlyTextWorksRoles, ...drawnWorksRoles, ...editionAuthorRoles ])
export const authorRolePropertiesSet = new Set(authorRoleProperties)

type AuthorRoleProperty = typeof authorRoleProperties[number]

const preferredAuthorRolesPropertiesPerWorkType = {
  // works
  'wd:Q1004': drawnWorksRoles, // comics
  'wd:Q2831984': drawnWorksRoles, // comic book album
  'wd:Q8274': manga, // manga
  'wd:Q562214': drawnWorksRoles, // manhwa
  'wd:Q725377': drawnWorksRoles, // graphic novel

  // series
  'wd:Q14406742': drawnWorksRoles, // comic book series
  'wd:Q21198342': manga, // manga series
  'wd:Q74262765': drawnWorksRoles, // manhwa series
} as const

export function getWorkPreferredAuthorRolesProperties (entity: SerializedEntity | EntityDraft) {
  const P31 = entity.claims['wdt:P31'] || []
  const preferredAuthorRolesProperties = P31.flatMap(getP31PreferredAuthorRolesProperties) as AuthorRoleProperty[]
  const usedAuthorRolesProperties = authorRoleProperties.filter(property => isNonEmptyArray(entity.claims[property]))
  return uniq(preferredAuthorRolesProperties.concat(usedAuthorRolesProperties)).sort(byDisplayOrder) as AuthorRoleProperty[]
}

const byDisplayOrder = (a, b) => authorRoleProperties.indexOf(a) - authorRoleProperties.indexOf(b)

function getP31PreferredAuthorRolesProperties (P31value: WdEntityUri) {
  return preferredAuthorRolesPropertiesPerWorkType[P31value] || mostlyTextWorksRoles
}
